import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import {
  MultiSelectScope,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';

import { Table } from '../components/Table';

export interface UseCheckboxColumnOptions<TData> {
  data: TData[];
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: TData) => string | null | undefined;
  getItemId?: (data: TData) => string | undefined;
  isSelectionDisabled?: (data: TData) => string | undefined;
}

export function useCheckboxColumn<TData>({
  data,
  alwaysShowCheckbox,
  showSelectAllCheckbox = true,
  getGroupId,
  getItemId,
  isSelectionDisabled,
}: UseCheckboxColumnOptions<TData>) {
  const { selectionMode, getSelection } = useMultiSelectionContext();
  const selections = getSelection();

  // Derive this table's groupId from its data
  const tableGroupId = useMemo(
    () => (data.length > 0 && getGroupId ? getGroupId(data[0]) : undefined),
    [data, getGroupId]
  );

  // Determine if checkbox should be shown
  const showCheckbox = useMemo(
    () =>
      alwaysShowCheckbox ||
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      (selectionMode?.mode === MultiSelectScope.SingleGroup &&
        selectionMode?.groupId === tableGroupId) ||
      selections.isSingleGroupMultiSelect,
    [
      alwaysShowCheckbox,
      selectionMode?.mode,
      selectionMode?.groupId,
      tableGroupId,
      selections.isSingleGroupMultiSelect,
    ]
  );

  // Create checkbox column definition
  const checkboxColumn: ColumnDef<TData> = useMemo(
    () => ({
      id: 'checkbox',
      header: ({ table }) => {
        return showSelectAllCheckbox ? (
          <Table.SelectAllCheckbox
            showSelectAllCheckbox={showSelectAllCheckbox}
            table={table}
            getGroupId={getGroupId}
            getItemId={getItemId}
          />
        ) : null;
      },
      cell: ({ row, table }) => (
        <Table.RowCheckbox
          row={row}
          table={table}
          getGroupId={getGroupId}
          getItemId={getItemId}
          isSelectionDisabled={isSelectionDisabled}
        />
      ),
      size: 26,
      enableSorting: false,
      enableResizing: false,
      enableColumnFilter: false,
    }),
    [getGroupId, getItemId, isSelectionDisabled, showSelectAllCheckbox]
  );

  return {
    showCheckbox,
    checkboxColumn,
  };
}
